<template>
	<n-modal
		v-model:show="show"
		preset="card"
		:title="modalTitle"
		segmented
		class="w-[90vw]! max-w-133!"
		@after-leave="appointmentFormRef?.resetForm()"
	>
		<AppointmentForm
			ref="appointmentFormRef"
			:active="show"
			:appointment="props.appointment"
			:initial-datetime="props.initialDatetime"
			:initial-end-datetime="props.initialEndDatetime"
			:initial-appointment-type="props.initialAppointmentType"
			:initial-all-day="props.initialAllDay"
			:initial-duration="props.initialDuration"
			:initial-office-id="props.initialOfficeId"
			:initial-patient-id="props.initialPatientId"
			:patient-readonly="props.patientReadonly"
			:show-appointment-type-tabs="props.showAppointmentTypeTabs"
			@saved="handleSaved()"
			@deleted="handleDeleted()"
		/>

		<template #footer>
			<div class="flex items-center justify-between gap-2">
				<JMButton
					v-if="props.appointment"
					type="secondary"
					:loading="appointmentFormRef?.deleting"
					:disabled="appointmentFormRef?.saving"
					@click="appointmentFormRef?.deleteAppointment()"
				>
					{{ $t(`appointments.common.delete_appointment`) }}
				</JMButton>
				<div v-else />

				<div class="flex gap-2">
					<JMButton
						type="secondary"
						:disabled="appointmentFormRef?.saving || appointmentFormRef?.deleting"
						@click="show = false"
					>
						{{ $t(`common.misc.cancel`) }}
					</JMButton>
					<JMButton
						type="default"
						:loading="appointmentFormRef?.saving"
						:disabled="appointmentFormRef?.deleting"
						@click="appointmentFormRef?.submit()"
					>
						{{ $t(`common.misc.confirm`) }}
					</JMButton>
				</div>
			</div>
		</template>
	</n-modal>
</template>

<script setup lang="ts">
import type { I18nString } from "@/lang/config"
import type { Appointment, AppointmentFormType } from "@/types/appointment"
import { NModal } from "naive-ui"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import AppointmentForm from "@/components/calendar/AppointmentForm.vue"
import JMButton from "@/components/common/JMButton.vue"

const props = withDefaults(
	defineProps<{
		appointment?: Appointment | null
		initialDatetime?: number | null
		initialEndDatetime?: number | null
		initialAppointmentType?: AppointmentFormType | null
		initialAllDay?: boolean | null
		initialDuration?: number | null
		initialOfficeId?: number | null
		initialPatientId?: number | null
		patientReadonly?: boolean
		showAppointmentTypeTabs?: boolean
	}>(),
	{
		appointment: null,
		initialDatetime: null,
		initialEndDatetime: null,
		initialAppointmentType: null,
		initialAllDay: null,
		initialDuration: null,
		initialOfficeId: null,
		initialPatientId: null,
		patientReadonly: false,
		showAppointmentTypeTabs: true
	}
)

const emit = defineEmits<{
	saved: []
	deleted: []
}>()

const show = defineModel<boolean>("show", { default: false })

const { t } = useI18n()

const appointmentFormRef = ref<InstanceType<typeof AppointmentForm> | null>(null)

const modalTitle = computed<string>(() => {
	const isEdit = !!props.appointment
	const isVisit = (appointmentFormRef.value?.appointmentType ?? "visit") === "visit"

	if (isEdit) {
		return t<I18nString>(isVisit ? `appointments.common.edit_visit` : `appointments.common.edit_event`)
	}

	return t<I18nString>(isVisit ? `appointments.common.add_visit` : `appointments.common.add_event`)
})

function handleSaved() {
	show.value = false
	emit("saved")
}

function handleDeleted() {
	show.value = false
	emit("deleted")
}
</script>
